import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/button/Button";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { LargeModal } from "../../components/modal/ModalUI";
import { largeModalShowHide ,setTitle } from "../../features/modalSlice";
import { can } from "../../utils/permission.util";

// ✅ from userSlice (as you said)
import {
  getRoleList,
  getRoleById,
  setRoleFlag,
  setRoleFormData,
  deleteRole,
  setDeleteRoleId,
} from "../../features/userSlice";

import RoleModal from "./createRoleModal";
import RoleDeleteConfirmModal from "./deleteRoleModal";
import {
  RowActionButton,
  RowActionIcons,
  RowActions,
} from "../../components/data-table/RowActions";

const roleData = {
  id: "",
  role: "",
  description: "",
  menuIds: [],
};

const Role = () => {
  const dispatch = useDispatch();
  const { roleList, roleTotal, roleError, isSpin, isBtnLoad, deleteRoleId } = useSelector(
    (state) => state.user,
  );

  // local delete modal state (keeps your existing modal system untouched)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // ---------- Filters & Pagination ----------
  const [searchText, setSearchText] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [retryTick, setRetryTick] = useState(0);

  const requestSeq = useRef(0);

  const rows = useMemo(
    () => (Array.isArray(roleList) ? roleList : []),
    [roleList],
  );

  const total = useMemo(() => Number(roleTotal || 0), [roleTotal]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((Number(total) || 0) / Number(limit || 1)));
  }, [total, limit]);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const delay = searchText.trim() ? 400 : 0;
    const timer = setTimeout(() => {
      if (seq !== requestSeq.current) return;
      dispatch(
        getRoleList({
          searchText: searchText.trim() || undefined,
          limit: Number(limit),
          page,
        }),
      );
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [dispatch, searchText, limit, page, retryTick]);

  // ---------- Actions ----------
  const addNewRole = () => {
    dispatch(setRoleFlag("add"));
    // dispatch(setRoleFormData(roleData));
    dispatch(setRoleFormData({ id: "", role: "", description: "", menuPermissions: [] }));

    dispatch(largeModalShowHide());
    dispatch(setTitle("Add New Role"));
  };

  const editRole = async (id) => {
    try {
      if (!id) throw new Error("Role not found");

      // Fetch role details (should include menuIds)
      const resp = await dispatch(getRoleById({ id })).unwrap();

      // adapt common API shapes safely
      const data = resp?.result || resp?.data || resp?.role || resp || {};

      const formData = {
        ...roleData,
        id: data?.id || id,
        role: data?.role || data?.name || "",
        description: data?.description || "",
        menuPermissions: Array.isArray(data?.menuPermissions)
          ? data.menuPermissions
          : [],
      };

      dispatch(setRoleFlag("update"));
      dispatch(setRoleFormData(formData));
      dispatch(largeModalShowHide());
      dispatch(setTitle("Edit Role"));
    } catch (err) {
      // getRoleById already reports the failure.
    }
  };

  const openDelete = (id) => {
    if (!id) return;
    dispatch(setDeleteRoleId(id));
    setDeleteModalOpen(true);
  };

  const closeDelete = () => {
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    try {
      const id = deleteRoleId;
      if (!id) return;

      await dispatch(deleteRole({ id })).unwrap();
      closeDelete();
    } catch (err) {
      // deleteRole already reports the failure.
    }
  };

  const onPrev = () => setPage((p) => Math.max(1, p - 1));
  const onNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <main id="main" className="main">
      <Breadcrumb page="Role List" />

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Role List</h5>

                  <div className="d-flex gap-2">
                    {can("/roles", "create") && (
                      <Button
                        className="btn btn-outline-primary btn-sm fs-7"
                        title="Add Role"
                        onClick={addNewRole}
                      >
                        <i className="fa-solid fa-plus" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Search */}
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <div className="input-group" style={{ maxWidth: 320 }}>
                    <span className="input-group-text">
                      <i className="fa fa-search" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by role"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th scope="col" className="text-center fs-7">
                          Role
                        </th>
                        <th scope="col" className="text-center fs-7">
                          Description
                        </th>
                                              {(can("/roles", "create") || can("/roles", "delete")) && (
                        <th scope="col" className="text-center fs-7">
                          Action
                        </th>
                                              )}
                      </tr>
                    </thead>

                    <tbody>
                      {roleError ? (
                        <tr>
                          <td className="text-center fs-7" colSpan={3}>
                            <div>{roleError}</div>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm mt-2"
                              onClick={() => setRetryTick((n) => n + 1)}
                            >
                              Retry
                            </button>
                          </td>
                        </tr>
                      ) : rows && rows.length ? (
                        rows.map((obj, i) => {
                          const isAdminRole =
                            String(obj?.role || obj?.name || "")
                              .trim()
                              .toLowerCase() === "admin";

                          return (
                            <tr key={`roleList-${obj?.id ?? i}`}>
                              <td className="text-center fs-7">
                                {obj?.role || obj?.name || "-"}
                              </td>

                              <td className="text-center fs-7">
                                {obj?.description || "-"}
                              </td>
                      {(can("/roles", "create") || can("/roles", "delete")) && (

                              <td className="text-center fs-7">
                                <RowActions>
                                  {can("/roles", "update") && (
                                    <RowActionButton
                                      label={
                                        isAdminRole
                                          ? "Admin role can't be edited"
                                          : "Edit"
                                      }
                                      iconClass={RowActionIcons.edit}
                                      variant="outline-success"
                                      disabled={isAdminRole}
                                      onClick={() => {
                                        if (isAdminRole) return;
                                        editRole(obj?.id || null);
                                      }}
                                    />
                                  )}
                                  {can("/roles", "delete") && (
                                    <RowActionButton
                                      label={
                                        isAdminRole
                                          ? "Admin role can't be deleted"
                                          : "Delete"
                                      }
                                      iconClass={RowActionIcons.delete}
                                      variant="outline-danger"
                                      disabled={isAdminRole}
                                      onClick={() => {
                                        if (isAdminRole) return;
                                        openDelete(obj?.id || null);
                                      }}
                                    />
                                  )}
                                </RowActions>
                              </td>
                      )}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td className="text-center fs-7" colSpan={3}>
                            {isSpin ? "Loading..." : "No roles found"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-end align-items-center gap-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={limit}
                    onChange={(e) => {
                      setPage(1);
                      setLimit(Number(e.target.value));
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>

                  <Button
                    className="btn btn-outline-secondary btn-sm fs-7"
                    disabled={page <= 1}
                    onClick={onPrev}
                  >
                    Previous
                  </Button>

                  <span className="px-2 fs-7">
                    Page {totalPages ? page : 0} of {totalPages}
                  </span>

                  <Button
                    className="btn btn-outline-secondary btn-sm fs-7"
                    disabled={page >= totalPages}
                    onClick={onNext}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LargeModal>
        <RoleModal />
      </LargeModal>

      <RoleDeleteConfirmModal
        show={deleteModalOpen}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        isLoading={isBtnLoad}
        roleId={deleteRoleId}
      />
    </main>
  );
};

export default Role;
